import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ArrowSquareIn, ArrowSquareOut, House, Coffee, Play, DownloadSimple, PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

export default function Attendance() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [records, setRecords] = useState([]);
  const [today, setToday] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [workMode, setWorkMode] = useState("Office");
  const [mySummary, setMySummary] = useState(null);

  const isEmployee = user?.role === "employee";
  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);

  const loadMy = async () => {
    try {
      const { data } = await api.get("/attendance/my-today");
      setMySummary(data);
    } catch {
      setMySummary(null);
    }
  };

  const load = async () => {
    if (!activeCompanyId) return;
    const [r, t, e] = await Promise.all([
      api.get("/attendance", { params: { company_id: activeCompanyId } }),
      api.get("/attendance/today", { params: { company_id: activeCompanyId } }),
      api.get("/employees", { params: { company_id: activeCompanyId, limit: 500 } }),
    ]);
    setRecords(r.data);
    setToday(t.data);
    setEmployees(e.data.items);
    if (!selectedEmp && e.data.items.length) {
      const me = e.data.items.find(x => x.id === user?.employee_id) || e.data.items[0];
      setSelectedEmp(me.id);
    }
    await loadMy();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const checkIn = async () => {
    try {
      await api.post("/attendance/check-in", {
        employee_id: selectedEmp,
        company_id: activeCompanyId,
        work_mode: workMode,
      });
      toast.success("Checked in");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check-in failed");
    }
  };

  const checkOut = async () => {
    try {
      await api.post("/attendance/check-out", { employee_id: selectedEmp, company_id: activeCompanyId });
      toast.success("Checked out");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check-out failed");
    }
  };

  const breakIn = async () => {
    try {
      const { data } = await api.post("/attendance/break-in", {
        employee_id: selectedEmp, company_id: activeCompanyId,
      });
      toast.success(`Break started · ${data.used_minutes}/${data.max_minutes} min used`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Break-in failed");
    }
  };

  const breakOut = async () => {
    try {
      const { data } = await api.post("/attendance/break-out", {
        employee_id: selectedEmp, company_id: activeCompanyId,
      });
      if (data.capped) {
        toast.warning(`Break capped at ${data.max_minutes} min limit`);
      } else {
        toast.success(`Break ended · ${data.used_minutes}/${data.max_minutes} min used today`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Break-out failed");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/attendance/export-excel", {
        params: { company_id: activeCompanyId }, responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "attendance.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const setOvertime = async (rec) => {
    const cur = rec.overtime_minutes || 0;
    const v = window.prompt(`Overtime minutes for ${rec.date}`, String(cur));
    if (v === null) return;
    const mins = parseInt(v || "0");
    if (Number.isNaN(mins) || mins < 0) {
      toast.error("Enter a non-negative integer");
      return;
    }
    try {
      await api.put(`/attendance/${rec.id}`, { overtime_minutes: mins });
      toast.success("Overtime updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const presentCount = today.filter(t => t.status !== "absent").length;
  const lateCount = today.filter(t => t.status === "late").length;
  const wfhCount = today.filter(t => t.status === "wfh").length;

  const myBreakUsed = mySummary?.break_used_minutes ?? 0;
  const myBreakMax = mySummary?.break_max_minutes ?? 45;
  const myBreakActive = !!mySummary?.active_break;
  const myRec = mySummary?.record;
  const breakPct = Math.min(100, Math.round((myBreakUsed / myBreakMax) * 100));
  const workedMin = mySummary?.worked_minutes ?? 0;
  const requiredMin = mySummary?.required_minutes ?? 480;
  const pendingMin = mySummary?.pending_minutes ?? 480;
  const workPct = Math.min(100, Math.round((workedMin / requiredMin) * 100));
  const toHrMin = (m) => `${Math.floor(m / 60)}h ${m % 60}m`;

  return (
    <div data-testid="attendance-page">
      <PageHeader eyebrow={activeCompany?.code} title="Attendance" subtitle="Daily check-in/out, breaks (45 min/day), corrections and reports.">
        {canEdit && (
          <Button onClick={exportExcel} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="export-attendance-btn">
            <DownloadSimple size={14} className="mr-1.5" /> Excel
          </Button>
        )}
        <Button onClick={checkIn} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-xs uppercase tracking-wider" data-testid="check-in-btn">
          <ArrowSquareIn size={14} className="mr-1.5" /> Check-in
        </Button>
        <Button onClick={checkOut} variant="outline" className="rounded-sm text-xs uppercase tracking-wider" data-testid="check-out-btn">
          <ArrowSquareOut size={14} className="mr-1.5" /> Check-out
        </Button>
        <Button
          onClick={breakIn}
          variant="outline"
          className="rounded-sm text-xs uppercase tracking-wider border-[#FAB005] text-[#946200] hover:bg-[#FFF7E0]"
          data-testid="break-in-btn"
          disabled={myBreakActive}
        >
          <Coffee size={14} className="mr-1.5" /> Break-in
        </Button>
        <Button
          onClick={breakOut}
          variant="outline"
          className="rounded-sm text-xs uppercase tracking-wider border-[#12B886] text-[#0B6F55] hover:bg-[#E6FFF8]"
          data-testid="break-out-btn"
          disabled={!myBreakActive}
        >
          <Play size={14} className="mr-1.5" /> Break-out
        </Button>
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
          <StatCell label="Present today" value={presentCount} accent="text-[#12B886]" testId="att-present" />
          <StatCell label="Late marks" value={lateCount} accent="text-[#FAB005]" testId="att-late" />
          <StatCell label="Working from home" value={wfhCount} accent="text-[#2563EB]" testId="att-wfh" />
          <StatCell label="Records (30d)" value={records.length} testId="att-total" />
        </div>

        {mySummary?.linked && (
          <Section title="My day">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Check-in</div>
                <div className="font-mono text-xl mt-1" data-testid="my-checkin">{fmt(myRec?.check_in)}</div>
              </div>
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Check-out</div>
                <div className="font-mono text-xl mt-1" data-testid="my-checkout">{fmt(myRec?.check_out)}</div>
              </div>
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Status</div>
                <div className="text-base font-semibold uppercase mt-1" data-testid="my-status">
                  {myRec?.status || (myBreakActive ? "on break" : "—")}
                </div>
                {mySummary?.shift?.name && (
                  <div className="text-[10px] text-[#64748B] mt-1 font-mono">
                    {mySummary.shift.name} · {mySummary.shift.start_time}–{mySummary.shift.end_time}
                  </div>
                )}
              </div>
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Work hours</div>
                  <div className="text-xs font-mono" data-testid="my-work-budget">{toHrMin(workedMin)} / {toHrMin(requiredMin)}</div>
                </div>
                <div className="mt-3 h-2 w-full bg-[#F1F5F9] rounded-sm overflow-hidden">
                  <div
                    className={`h-full ${workedMin >= requiredMin ? "bg-[#12B886]" : "bg-[#2563EB]"}`}
                    style={{ width: `${workPct}%` }}
                  />
                </div>
                <div className="text-[11px] text-[#64748B] mt-2">
                  Pending: <span className="font-mono">{toHrMin(pendingMin)}</span>
                </div>
              </div>
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">Break budget</div>
                  <div className="text-xs font-mono" data-testid="my-break-budget">{myBreakUsed} / {myBreakMax} min</div>
                </div>
                <div className="mt-3 h-2 w-full bg-[#F1F5F9] rounded-sm overflow-hidden">
                  <div
                    className={`h-full ${myBreakUsed >= myBreakMax ? "bg-[#F54236]" : myBreakActive ? "bg-[#FAB005]" : "bg-[#12B886]"}`}
                    style={{ width: `${breakPct}%` }}
                  />
                </div>
                {myBreakActive && (
                  <div className="text-[11px] text-[#946200] mt-2">Break in progress…</div>
                )}
              </div>
            </div>
          </Section>
        )}

        {mySummary && !mySummary.linked && isEmployee && (
          <div className="border border-[#FAB005] bg-[#FFF7E0] text-[#7A5A00] text-sm rounded-sm p-4">
            Your login is not linked to an employee profile yet. Ask HR to link your user to an employee record (matching email auto-links automatically).
          </div>
        )}

        <Section title="Check-in panel">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569] block mb-1">Employee</label>
              <Select value={selectedEmp} onValueChange={setSelectedEmp} disabled={isEmployee}>
                <SelectTrigger className="w-64 rounded-sm" data-testid="att-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569] block mb-1">Work mode</label>
              <Select value={workMode} onValueChange={setWorkMode}>
                <SelectTrigger className="w-36 rounded-sm" data-testid="att-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Office"><House size={12} className="inline mr-1.5"/>Office</SelectItem>
                  <SelectItem value="WFH">WFH</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        <Section title="Today's attendance" dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead><TableHead>Break</TableHead><TableHead>Worked</TableHead>
              <TableHead>OT</TableHead><TableHead>Status</TableHead><TableHead>Mode</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {today.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#64748B] py-8">No attendance for today</TableCell></TableRow>}
              {today.map((t) => {
                const emp = empMap[t.employee_id] || {};
                const hh = Math.floor((t.total_work_minutes || 0) / 60);
                const mm = (t.total_work_minutes || 0) % 60;
                return (
                  <TableRow key={t.id} data-testid={`att-today-${t.id}`}>
                    <TableCell className="font-mono text-xs text-[#2563EB]">{emp.employee_code}</TableCell>
                    <TableCell>{emp.full_name}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(t.check_in)}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(t.check_out)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.total_break_minutes || 0}m</TableCell>
                    <TableCell className="font-mono text-xs">{t.check_out ? `${hh}h ${mm}m` : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <span>{t.overtime_minutes || 0}m</span>
                      {canEdit && (
                        <button onClick={() => setOvertime(t)} className="ml-1 text-[#2563EB] hover:underline align-middle" title="Edit overtime" data-testid={`edit-ot-${t.id}`}>
                          <PencilSimple size={11} className="inline"/>
                        </button>
                      )}
                    </TableCell>
                    <TableCell><span className={`text-[10px] uppercase tracking-wider font-semibold ${t.status === "late" ? "text-[#FAB005]" : t.status === "wfh" ? "text-[#2563EB]" : t.status === "short" || t.status === "half_day" ? "text-[#F54236]" : "text-[#12B886]"}`}>{t.status}</span></TableCell>
                    <TableCell className="text-xs">{t.work_mode}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Section>

        <Section title="Recent records" dense>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead><TableHead>Break</TableHead><TableHead>Worked</TableHead>
              <TableHead>OT</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {records.slice(0, 40).map((t) => {
                const emp = empMap[t.employee_id] || {};
                const hh = Math.floor((t.total_work_minutes || 0) / 60);
                const mm = (t.total_work_minutes || 0) % 60;
                return (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs">{t.date}</TableCell>
                    <TableCell>{emp.full_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(t.check_in)}</TableCell>
                    <TableCell className="font-mono text-xs">{fmt(t.check_out)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.total_break_minutes || 0}m</TableCell>
                    <TableCell className="font-mono text-xs">{t.check_out ? `${hh}h ${mm}m` : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <span>{t.overtime_minutes || 0}m</span>
                      {canEdit && (
                        <button onClick={() => setOvertime(t)} className="ml-1 text-[#2563EB] hover:underline align-middle" title="Edit overtime">
                          <PencilSimple size={11} className="inline"/>
                        </button>
                      )}
                    </TableCell>
                    <TableCell><span className="text-[10px] uppercase tracking-wider font-semibold">{t.status}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Section>
      </PageBody>
    </div>
  );
}
